import { compact, get, merge, pick, uniq } from 'lodash';

import { Locale } from '@/i18n/locales';
import { ClientSafeConfig, PartialTenantConfig, TenantConfig } from '@/lib/types/tenant';

const ASSISTANT_NAME_KEY = 'chat.assistantName';
const ASSISTANT_NAME_PLACEHOLDER = /\{assistantName\}/g;

export function getLocalizedValue<T>(
  config: TenantConfig | ClientSafeConfig,
  currentLocale: Locale,
  keyPath: string,
): T | null {
  const { meta, translations } = config;
  const { language: defaultLocale, availableLocales = [defaultLocale] } = meta;

  const fallbackChain = uniq(compact([currentLocale, defaultLocale, ...availableLocales]));

  for (const locale of fallbackChain) {
    const value = get(translations[locale], keyPath);
    if (value !== undefined) {
      return interpolateAssistantName({ value, config, currentLocale, keyPath }) as T;
    }
  }

  return null;
}

/**
 * Substitutes `{assistantName}` in any localized string with the tenant's
 * `chat.assistantName`, also resolved through the same locale fallback chain.
 * Lets every tenant text field (meta.title, sidebarTitle, tooltipText,
 * welcome*, etc.) reference the brand once via the placeholder instead of
 * repeating the literal string per locale.
 */
function interpolateAssistantName({
  value,
  config,
  currentLocale,
  keyPath,
}: {
  value: unknown;
  config: TenantConfig | ClientSafeConfig;
  currentLocale: Locale;
  keyPath: string;
}): unknown {
  if (typeof value !== 'string') return value;
  // Recursion guard: resolving `chat.assistantName` itself must not loop back.
  if (keyPath === ASSISTANT_NAME_KEY) return value;
  return substituteAssistantName({ value, config, currentLocale });
}

/** For text that doesn't come from `getLocalizedValue`, which already does this. */
export function substituteAssistantName({
  value,
  config,
  currentLocale,
}: {
  value: string;
  config: TenantConfig | ClientSafeConfig;
  currentLocale: Locale;
}): string {
  if (!value.includes('{assistantName}')) return value;
  const assistantName = getLocalizedValue<string>(config, currentLocale, ASSISTANT_NAME_KEY);
  if (!assistantName) return value;
  return value.replace(ASSISTANT_NAME_PLACEHOLDER, assistantName);
}

interface MergeLocaleOverridesParams {
  base: Record<string, unknown>;
  overrides: Record<string, unknown> | undefined;
  locale: string;
}

/**
 * Layers a tenant's config-level translation overrides onto an already-merged message base:
 * `en` first (the always-present floor), then the target locale on top of that. Both the route
 * that builds the on-demand locale dictionary and the client provider that falls back to it call
 * this so their merge order can't drift apart — whether to call it at all is the caller's call.
 */
export function mergeLocaleOverrides({
  base,
  overrides,
  locale,
}: MergeLocaleOverridesParams): Record<string, unknown> {
  return merge(
    {},
    base,
    overrides?.['en'] ?? {},
    locale !== 'en' ? (overrides?.[locale] ?? {}) : {},
  );
}

export function getLocalesFromConfig(config: PartialTenantConfig): string[] {
  const { translations } = config;
  if (!translations) return [];
  return Object.keys(translations).filter(key => translations[key as keyof typeof translations]);
}

interface PickTranslationLocalesParams {
  translations: TenantConfig['translations'] | undefined;
  locales: Array<string | undefined>;
}

export function pickTranslationLocales({
  translations,
  locales,
}: PickTranslationLocalesParams): TenantConfig['translations'] {
  return pick(translations ?? {}, uniq(compact(locales))) as TenantConfig['translations'];
}
